/** Reseller/customer login page (the routed implementation).
 *
 * UX review B1/B4: uses the shared axios client (same-origin proxy + CSRF +
 * httpOnly cookies) instead of a raw fetch with a hardcoded localhost URL,
 * adds a password visibility toggle and a session-expired notice.
 */
import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock as LockIcon } from "@mui/icons-material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { authApi, clearResellerToken } from "../../api/client";

/** Reseller login page component. */
export default function ResellerLogin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const expired = searchParams.get("expired") === "1";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  /** Dismiss the session-expired banner. */
  const dismissExpired = () => {
    searchParams.delete("expired");
    setSearchParams(searchParams, { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Cookie-based auth via the shared client; token body kept as a
      // localStorage fallback so the portal works even if cookies are lost.
      const data = (await authApi.resellerLogin(username, password)).data;

      if (data.access_token) {
        localStorage.setItem("reseller_token", data.access_token);
      }
      localStorage.setItem("reseller_user_id", data.user_id);
      localStorage.setItem("reseller_username", data.username);
      localStorage.setItem("reseller_role", data.role);
      if (data.reseller_id) {
        localStorage.setItem("reseller_id", data.reseller_id);
      }

      dismissExpired();

      // Wave 5C: customers land on their own portal
      if (data.role === "customer") {
        navigate("/customer/portal");
      } else {
        navigate("/reseller/dashboard");
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message;
      setError(
        typeof detail === "string" && detail
          ? detail
          : "Failed to login. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  /** Toggle password visibility (UX review B4). */
  const handleTogglePassword = () => setShowPassword((prev) => !prev);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",
              borderRadius: "50%",
              p: 2,
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 40, color: "white" }} />
          </Box>

          <Typography component="h1" variant="h4" gutterBottom>
            Reseller Portal
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to manage your reseller account
          </Typography>

          {expired && (
            <Alert severity="warning" sx={{ width: "100%", mb: 2 }} onClose={dismissExpired}>
              Your session has expired. Please sign in again.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username or Email"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={handleTogglePassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Contact your administrator if you forgot your password
          </Typography>
        </Paper>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button color="inherit" onClick={() => navigate("/login")} sx={{ color: "text.secondary" }}>
            Admin Portal Login
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
